import { useState, useEffect } from "react";
import { Typography, TextField, Checkbox, FormControlLabel } from "@material-ui/core";
import SmarthopConfirmDialog from "@smarthop/form/SmarthopConfirmDialog";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { createMarketMonitor } from "app/services/searchServices";
import { showSnackbar } from "app/main/utils/snackbarUtil";
import { getUserId } from "app/services/LoginService";
import { incrementDataRevision } from "app/store/tools/revisionSlice";

function AgentConfigurationView({
	open,
	truckInfo,
	carrierId,
	truckId,
	planId,
	chainId,
	searchMode = "relay_chain",
	onClose,
	onDone,
}) {
	const dispatch = useDispatch();
	const snackbar = useSnackbar();
	const userId = getUserId();

	const [phoneNumber, setPhoneNumber] = useState("");
	const [email, setEmail] = useState("");
	const [phoneError, setPhoneError] = useState("");
	const [emailError, setEmailError] = useState("");
	const [forceAutobook, setForceAutobook] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			setPhoneNumber("");
			setEmail("");
			setPhoneError("");
			setEmailError("");
			setForceAutobook(false);
		}
	}, [open]);

	const validatePhone = (value) => {
		// If empty, it's valid (optional field)
		if (!value || value.trim() === "") {
			return "";
		}
		// Basic phone validation (allows various formats)
		const phoneRegex = /^[0-9\s\-\+\(\)ext\.x]+$/i;
		if (!phoneRegex.test(value)) {
			return "Please enter a valid phone number";
		}
		return "";
	};

	const validateEmail = (value) => {
		// If empty, it's valid (optional field)
		if (!value || value.trim() === "") {
			return "";
		}
		// Basic email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(value)) {
			return "Please enter a valid email address";
		}
		return "";
	};

	const handlePhoneChange = (e) => {
		const value = e.target.value;
		setPhoneNumber(value);
		const validationError = validatePhone(value);
		setPhoneError(validationError);
	};

	const handleEmailChange = (e) => {
		const value = e.target.value;
		setEmail(value);
		const validationError = validateEmail(value);
		setEmailError(validationError);
	};

	const handleConfirm = async () => {
		const phoneValidationError = validatePhone(phoneNumber);
		const emailValidationError = validateEmail(email);

		if (phoneValidationError) {
			setPhoneError(phoneValidationError);
			return;
		}
		if (emailValidationError) {
			setEmailError(emailValidationError);
			return;
		}

		setIsSubmitting(true);

		try {
			const result = await createMarketMonitor(userId, {
				carrierId,
				truckId,
				planId,
				chainId,
				searchMode,
				phoneNumber: phoneNumber.trim() || undefined,
				email: email.trim() || undefined,
				forceAutobook,
				currentLocation: truckInfo?.currentLocation,
				availableDate: truckInfo?.availableDate,
				deliveryDate: truckInfo?.deliveryDate,
			});

			if (result.status === "LAUNCHED") {
				if (result.new) {
					showSnackbar(snackbar, "Agent Started Successfully", "success");
				} else {
					showSnackbar(snackbar, "Agent Restarted Successfully", "warning");
				}
				dispatch(incrementDataRevision({ event: "profileRevision" }));
			} else if (result.status === "RESTARTED") {
				showSnackbar(snackbar, "Restarted existing agent successfully", "warning");
			}

			onDone?.();
		} catch (err) {
			showSnackbar(snackbar, err.errors?.[0]?.message ?? err.message ?? "Oops, something went wrong...", "error");
		} finally {
			setIsSubmitting(false);
		}
	};

	const isValid = !phoneError && !emailError;

	return (
		<SmarthopConfirmDialog
			open={!!open}
			title="Configure AI Agent"
			message="Configure the AI agent to automatically search and negotiate loads for this truck"
			handleClose={onClose}
			handleAccept={handleConfirm}
			mode={"NORMAL"}
			acceptMsg="Start Agent"
			disableAccept={!isValid || isSubmitting}
			loading={isSubmitting}
		>
			<div className="flex flex-col gap-12 pt-8">
				{/* Truck Information */}
				{truckInfo && (
					<div className="flex flex-col gap-4 pb-8 border-b-1">
						<Typography className="text-12 text-gray-600">
							<strong>Truck:</strong> {truckInfo?.truckId || "N/A"}
						</Typography>
						<Typography className="text-12 text-gray-600">
							<strong>Carrier:</strong> {truckInfo?.carrierName || "N/A"}
						</Typography>
						{truckInfo?.currentLocation && (
							<Typography className="text-12 text-gray-600">
								<strong>Current Location:</strong> {truckInfo?.currentLocation}
							</Typography>
						)}
						{truckInfo?.availableDate && (
							<Typography className="text-12 text-gray-600">
								<strong>Available:</strong> {truckInfo?.availableDate}
							</Typography>
						)}
						{truckInfo?.deliveryDate && (
							<Typography className="text-12 text-gray-600">
								<strong>Delivery deadline:</strong> {truckInfo?.deliveryDate}
							</Typography>
						)}
					</div>
				)}

				{/* Phone Number Input */}
				<div className="flex flex-col gap-4">
					<Typography className="text-13 font-semibold">Contact Phone Number (Optional)</Typography>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						value={phoneNumber}
						onChange={handlePhoneChange}
						error={!!phoneError}
						helperText={phoneError || "Phone number for broker communications (optional)"}
						placeholder="(555) 123-4567"
						autoFocus
						InputProps={{
							className: "text-14",
						}}
					/>
				</div>

				{/* Email Input */}
				<div className="flex flex-col gap-4">
					<Typography className="text-13 font-semibold">Contact Email Address (Optional)</Typography>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						value={email}
						onChange={handleEmailChange}
						error={!!emailError}
						helperText={emailError || "Email address for broker communications (optional)"}
						placeholder="dispatcher@carrier.com"
						InputProps={{
							className: "text-14",
						}}
					/>
				</div>

				{/* Force Autobook Checkbox */}
				<div className="flex flex-col gap-4">
					<FormControlLabel
						control={
							<Checkbox checked={forceAutobook} onChange={(e) => setForceAutobook(e.target.checked)} color="primary" />
						}
						label={
							<div className="flex flex-col">
								<Typography className="text-13 font-semibold">Force Auto-Book</Typography>
								<Typography className="text-11 text-gray-600">Enable automatic booking settings</Typography>
							</div>
						}
					/>
				</div>

				{/* Additional Info */}
				<div className="flex flex-col pt-4">
					<Typography className="text-11 text-orange italic">
						*The AI agent will use this contact information when negotiating with brokers
					</Typography>
					<Typography className="text-11 text-orange italic mt-4">
						*The agent will automatically search for loads matching your truck's plan and contact brokers on your behalf
					</Typography>
				</div>
			</div>
		</SmarthopConfirmDialog>
	);
}

export default AgentConfigurationView;
